import "./App.scss";
import me from "./assets/img/me.png";
import toolsLight from "./assets/img/toolsLight.png";
import toolsDark from "./assets/img/toolsDark.png";
import github from "./assets/img/github.png";
import instagram from "./assets/img/instagram.png";
import linkedin from "./assets/img/linkedin.png";
import twitter from "./assets/img/twitter.png";
import youtube from "./assets/img/youtube.png";
import airagon from "./assets/img/airagon.png";
import spinninBeans from "./assets/img/spinninbeans.png";
import carMeetApp from "./assets/img/car-meet-app.png";
import focusRs from "./assets/img/focus-rs-full.png";
import crateCo from "./assets/img/crateco.png";
import sets from "./assets/img/sets.png";
import finSights from "./assets/img/finsights.jpg";
import stonkBotSiteImg from "./assets/img/stonk-bot-site-img.png";
import Form from "./Form.js";
import ProjectContainer from "./Projects.js";
import Carousel from "./Carousel.js";
import { DarkModeSwitch } from "react-toggle-dark-mode";
import { ThemeProvider } from "styled-components";
import { GlobalStyles } from "./globalStyles.js";
import { lightTheme, darkTheme } from "./Themes.js";
import { Link } from "react-scroll";
import SplashScreen from "./SplashScreen/SplashScreen.js";
import CustomScrollBar from "./CustomScrollBar/CustomScrollBar.js";
import MusicPlayer from "./MusicPlayer/MusicPlayer.js";
import Dashboard from "./Dashboard/Dashboard.js";
import Bio from "./Modal/Modal.js";
import ScriptsModal from "./ScriptsModal/ScriptsModal.js";
import Blog from "./Blog/Blog.jsx";
import MusicPage from "./MusicPage/MusicPage.js";
import React, { useState, useRef, useCallback } from "react";

// Moved outside the component as it doesn't depend on component state/props
const truncateDescription = (description, maxLength = 200) => {
  return description.length > maxLength
    ? description.substring(0, maxLength) + "..."
    : description;
};

// Initial project data (moved outside for clarity, could be in a separate file)
const initialProjects = [
  {
    id: 0,
    name: "Spinnin' Beans",
    description:
      "Spinnin Beans is a music discovery brand and digital platform.",
    preview: spinninBeans,
    githubLink: "",
    liveLink: "https://spinninbeans.com/",
  },
  {
    id: 1,
    name: "Aragon Stonk Bot",
    description:
      "Stonk Bot is an AI financial agent built on Mistral Large (24.11) Uses extensive learning to output the best plans for your future.",
    preview: stonkBotSiteImg,
    liveLink: "https://stonk-bot-site.vercel.app/",
  },
  {
    id: 2,
    name: "Spinnin' Beans - Crate.co Track Extractor",
    description:
      "A lightweight Chrome extension that simplifies the process of extracting track links from Crate.co playlists.",
    preview: crateCo,
    liveLink:
      "https://chromewebstore.google.com/detail/spinnin-beans-crateco-tra/obkchoadnpbikoebnbmeijmpffacckpi?hl=en",
  },
  {
    id: 3,
    name: "UndrGround (car-meet-app)",
    description:
      "My largest project yet. Built with Expo, Firebase, and React Native. A car meet app for car enthusiasts. Currently in development.",
    preview:
      "https://user-images.githubusercontent.com/69175998/159408917-9244cc8b-9718-468c-ba38-94b19fad2993.png",
    githubLink: "https://github.com/josharagon/car-meet-app",
    liveLink: "",
  },
  {
    id: 4,
    name: "FinSights",
    description: truncateDescription(
      "My First Machine Learning Project. This application leverages machine learning models to analyze insider trading data and predict stock performance based on governmental and corporate insider activities. The project integrates data from multiple sources, including Congressional, Senate, and House trading records, lobbying activities, government contracts, and off-exchange transactions. It combines this with historical stock price data to identify patterns and predict future stock price movements and the impact of insider trades."
    ),
    preview: finSights,
    githubLink: "https://github.com/josharagon/finsights",
    liveLink: "",
  },
  {
    id: 5,
    name: "Airagon",
    description: "A Sneaker Automation Application",
    preview: airagon,
    githubLink: "",
    liveLink: "",
  },
];

function App() {
  // State Hooks
  const [projects] = useState(initialProjects);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [theme, setTheme] = useState("dark");
  const [isDashboardActive, setIsDashboardActive] = useState(false);
  const [isScriptsModalOpen, setIsScriptsModalOpen] = useState(false);
  const [activeSite, setActiveSite] = useState("dev");
  const lastDevScrollPosition = useRef(0);
  const lastScrollPositions = useRef({ dev: 0, blog: 0, music: 0 });

  // Function to toggle theme and dark mode
  const themeToggler = useCallback(() => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    setIsDarkMode(newTheme === "dark");
  }, [theme]);

  // Render tool image based on theme
  const renderToolImg = () => {
    if (theme === "light") {
      return (
        <img src={toolsLight} className="my-toolbox" alt="toolbox light"></img>
      );
    } else {
      return (
        <img src={toolsDark} className="my-toolbox" alt="toolbox dark"></img>
      );
    }
  };

  // Function to toggle the dashboard view
  const toggleDashboardActive = useCallback(() => {
    if (!isDashboardActive) {
      document.body.classList.add("dashboard-active");
    } else {
      document.body.classList.remove("dashboard-active");
    }
    setIsDashboardActive((prev) => !prev);
  }, [isDashboardActive]);

  // Function to toggle the scripts modal
  const toggleScriptsModal = useCallback(() => {
    setIsScriptsModalOpen((prev) => !prev);
  }, []);

  // Function to switch between dev and blog sections
  const switchSite = useCallback(
    (site) => {
      // Save current scroll position for the outgoing site
      if (activeSite !== site) {
        lastScrollPositions.current[activeSite] = window.scrollY;
      }

      // Scroll to top for blog and music, or restore for dev
      if (site === "blog" || site === "music") {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "instant" });
        }, 0);
      } else if (site === "dev" && lastScrollPositions.current.dev) {
        setTimeout(() => {
          window.scrollTo({
            top: lastScrollPositions.current.dev,
            behavior: "instant",
          });
        }, 0);
      }
      // If switching to a site other than dev, and its previous scroll position is known, restore it
      // This part is a bit more complex if we want to restore scroll for blog/music too,
      // but for now, blog/music always go to top. Dev restores.
      // Consider if blog/music should also restore scroll if that's desired.

      setActiveSite(site);
    },
    [activeSite]
  );

  return (
    <>
      {isDashboardActive ? (
        <Dashboard onExitClick={toggleDashboardActive} />
      ) : (
        <ThemeProvider theme={theme === "light" ? lightTheme : darkTheme}>
          <GlobalStyles />
          {/* <SplashScreen /> */}
          <nav className="nav-bar">
            <div className="name-music-container">
              <p>Josh Aragon</p>
              {/* <MusicPlayer isDarkMode={isDarkMode} /> */}
              <div className="site-switcher">
                <span
                  className={`dev-tag ${activeSite === "dev" ? "active" : ""}`}
                  onClick={() => switchSite("dev")}
                >
                  &lt;/dev&gt;
                </span>
                <span
                  className={`blog-text ${
                    activeSite === "blog" ? "active" : ""
                  }`}
                  data-text="blog"
                  onClick={() => switchSite("blog")}
                >
                  blog
                </span>
                <span
                  className={`music-text ${
                    activeSite === "music" ? "active" : ""
                  }`}
                  onClick={() => switchSite("music")}
                  style={{ cursor: "pointer", marginLeft: "10px" }}
                >
                  music
                </span>
              </div>
            </div>
            <div className="nav-links">
              <div
                className={`nav-links-wrapper ${
                  activeSite !== "dev" ? "hidden" : ""
                }`}
              >
                <Link
                  activeClass="active"
                  to="about"
                  spy={true}
                  smooth={true}
                  offset={-70}
                  duration={800}
                >
                  <p>about me</p>
                </Link>
                <Link
                  activeClass="active"
                  to="projects"
                  spy={true}
                  smooth={true}
                  offset={-70}
                  duration={800}
                >
                  <p>my work</p>
                </Link>
                <Link
                  activeClass="active"
                  to="contact"
                  spy={true}
                  smooth={true}
                  offset={-70}
                  duration={800}
                >
                  <p>contact me</p>
                </Link>
              </div>
              <DarkModeSwitch
                style={{ marginBottom: ".35rem" }}
                checked={isDarkMode}
                size={30}
                onClick={themeToggler}
              />
            </div>
          </nav>
          {/* Add a container with transition classes */}
          <div className={`page-container ${activeSite}-active`}>
            <div
              className={`content-section dev-section ${
                activeSite === "dev" ? "active" : ""
              }`}
            >
              <section className="hello-container">
                <div className="hello-me">
                  <h1>hello!</h1>
                  <h2>I'm Josh, a software developer in Brighton, CO</h2>
                  <Link
                    activeClass="active"
                    to="contact"
                    spy={true}
                    smooth={true}
                    offset={-70}
                    duration={800}
                  >
                    <p className="work-with-me"> Work With Me</p>
                  </Link>
                </div>
                <div className="me-image-container">
                  <img src={me} className="me-image" height="500px" alt="me" />
                </div>
              </section>
              <section className="my-work" id="projects">
                <div className="projects-header">
                  <h1>my projects</h1>
                  <button
                    className="view-scripts-btn"
                    onClick={toggleScriptsModal}
                  >
                    View Python Scripts
                  </button>
                </div>
                <ProjectContainer projects={projects} theme={theme} />
              </section>
              <section className="my-work" id="projects">
                <h1>my design gallery</h1>
                <div className="carousel-holder">
                  <Carousel />
                </div>
              </section>
              <section className="about-me" id="about">
                <div className="tools-about">
                  {renderToolImg()}
                  <div className="more-about">
                    <h1 className="summary-head">more about me</h1>
                    <p className="my-summary">
                      Support Software engineer at SeachSpring. Strong passion
                      for learning. I found coding my freshman year of
                      highschool and fell in love. Outside of coding my main
                      passion is automobiles and DJing. I love going to the car
                      meets on the weekend. I also love to DJ to relax, it is a
                      constant search for new music and a way to express myself.
                    </p>
                    <Bio theme={theme} />
                    <div className="social-links">
                      <a
                        href="https://www.github.com/josharagon"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={github}
                          className="social-logo"
                          alt="my github"
                        ></img>
                      </a>
                      <a
                        href="https://www.linkedin.com/in/joshuajaragon/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={linkedin}
                          className="social-logo"
                          alt="my linkedin"
                        ></img>
                      </a>
                      <a
                        href="https://www.instagram.com/purecarmedia"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={instagram}
                          className="social-logo"
                          alt="my instagram"
                        ></img>
                      </a>
                    </div>
                  </div>
                </div>
              </section>
              <Form />
              {/* <CustomScrollBar /> */}
            </div>

            <div
              className={`content-section blog-section ${
                activeSite === "blog" ? "active" : ""
              }`}
            >
              <Blog />
            </div>
            {/* Correctly placed music-section as a sibling */}
            <div
              className={`content-section music-section ${
                activeSite === "music" ? "active" : ""
              }`}
            >
              <MusicPage />
            </div>
          </div>
          {/* ScriptsModal component moved here, outside of page-container's transformed children */}
          <ScriptsModal
            isOpen={isScriptsModalOpen}
            onClose={toggleScriptsModal}
            theme={theme}
          />
        </ThemeProvider>
      )}
    </>
  );
}

export default App;
